import * as React from 'react'
import {
  Lock,
  Sparkles,
  CheckCircle2,
  Building2,
  TrendingUp,
  Target,
  Lightbulb,
  ChevronDown,
  ChevronUp,
  ExternalLink,
} from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import type { EnrichmentResult } from './use-lead-detail'

// ─── Strategy descriptions ────────────────────────────────────────────────

const STRATEGY_DESCRIPTIONS: Record<string, string> = {
  Pain_Agitate:
    "Lead with the prospect's known pain point, then agitate it to drive urgency before offering your solution.",
  Mutual_Value:
    "Frame the outreach as a reciprocal exchange: what you can offer vs. what you're asking for.",
  Social_Proof:
    'Reference similar companies or client results to build credibility and reduce perceived risk.',
  Curiosity_Gap:
    'Open with an intriguing observation that creates an open loop the prospect wants to close.',
  Direct_Expert:
    'Position yourself as a domain expert sharing a useful insight, which builds authority fast.',
  Challenge:
    'Provoke a widely-held industry assumption to spark intellectual engagement.',
  Trojan_Horse:
    'Offer a free resource, audit, or template to start the conversation before pitching.',
  Mutual_Connection:
    'Leverage shared connections, groups, or events to create instant social context.',
}

// ─── Sub-components ───────────────────────────────────────────────────────

type CollapsibleNewsItemProps = {
  readonly title: string
  readonly summary: string
  readonly date: string
  readonly url: string
}

function CollapsibleNewsItem({ title, summary, date, url }: CollapsibleNewsItemProps) {
  const [expanded, setExpanded] = React.useState(false)

  return (
    <div className="border rounded-md p-3 space-y-1">
      <div className="flex items-start justify-between gap-2">
        <p className="text-xs font-medium leading-snug flex-1">{title}</p>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="shrink-0 text-muted-foreground hover:text-foreground transition-colors"
          aria-label={expanded ? 'Collapse' : 'Expand'}
        >
          {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        </button>
      </div>
      {expanded && (
        <>
          <p className="text-xs text-muted-foreground leading-relaxed">{summary}</p>
          <div className="flex items-center justify-between pt-1">
            <span className="text-[10px] text-muted-foreground">
              {new Date(date).toLocaleDateString(undefined, {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })}
            </span>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[10px] text-primary hover:underline flex items-center gap-1"
            >
              Read more
              <ExternalLink className="h-2.5 w-2.5" />
            </a>
          </div>
        </>
      )}
      {!expanded && (
        <p className="text-[10px] text-muted-foreground">
          {new Date(date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
        </p>
      )}
    </div>
  )
}

// ─── Unenriched state ─────────────────────────────────────────────────────

type UnenrichedStateProps = {
  readonly onRunEnrichment: () => void
  readonly isDisabledForPlan?: boolean
}

function UnenrichedState({ onRunEnrichment, isDisabledForPlan = false }: UnenrichedStateProps) {
  return (
    <div className="rounded-lg border border-dashed p-5 space-y-3 text-center">
      <div className="flex justify-center">
        <div className="rounded-full bg-muted p-3">
          <Lock className="h-5 w-5 text-muted-foreground" />
        </div>
      </div>
      <div className="space-y-1">
        <h4 className="text-sm font-semibold">Unlock AI Research</h4>
        <p className="text-xs text-muted-foreground">
          Enrichment surfaces company signals, pain points, and strategy recommendations.
        </p>
      </div>
      <ul className="text-left space-y-1 text-xs text-muted-foreground">
        {[
          'Company size, industry & funding stage',
          'Recent news & growth signals',
          'Likely pain points & ICP fit signals',
          'Recommended outreach strategy',
          'Competitor tool usage',
        ].map((item) => (
          <li key={item} className="flex items-center gap-2">
            <CheckCircle2 className="h-3 w-3 shrink-0 text-muted-foreground/60" />
            {item}
          </li>
        ))}
      </ul>

      {isDisabledForPlan ? (
        <div className="space-y-2">
          <Button size="sm" className="w-full text-xs" disabled>
            <Sparkles className="h-3.5 w-3.5 mr-1.5" />
            Run Enrichment
          </Button>
          <Badge variant="outline" className="text-[10px]">
            Agency plan required
          </Badge>
        </div>
      ) : (
        <Button size="sm" className="w-full text-xs" onClick={onRunEnrichment}>
          <Sparkles className="h-3.5 w-3.5 mr-1.5" />
          Run Enrichment
        </Button>
      )}
    </div>
  )
}

// ─── Loading state ────────────────────────────────────────────────────────

function EnrichmentLoadingSkeleton() {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Sparkles className="h-3.5 w-3.5 animate-pulse text-purple-500" />
        <span className="animate-pulse">Researching...</span>
      </div>
      <div className="space-y-2">
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-5/6" />
      </div>
      <div className="space-y-2">
        <Skeleton className="h-3 w-1/2" />
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-4/5" />
      </div>
      <div className="space-y-1">
        <Skeleton className="h-3 w-3/4" />
        <Skeleton className="h-3 w-2/3" />
        <Skeleton className="h-3 w-full" />
      </div>
    </div>
  )
}

// ─── Enriched state ───────────────────────────────────────────────────────

type EnrichedViewProps = {
  readonly data: EnrichmentResult
}

function EnrichedView({ data }: EnrichedViewProps) {
  const strategyDescription = STRATEGY_DESCRIPTIONS[data.recommended_strategy]

  return (
    <div className="space-y-5">
      {/* Company Info */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
          <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Company Info
          </h4>
        </div>
        <div className="rounded-lg border bg-muted/20 p-3 space-y-2">
          <p className="text-xs text-muted-foreground leading-relaxed">
            {data.company_info.description}
          </p>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
            <span className="text-muted-foreground">Size</span>
            <span className="font-medium">{data.company_info.size}</span>
            <span className="text-muted-foreground">Founded</span>
            <span className="font-medium">{data.company_info.founded}</span>
            <span className="text-muted-foreground">Location</span>
            <span className="font-medium">{data.company_info.hq_location}</span>
            <span className="text-muted-foreground">Industry</span>
            <span className="font-medium">{data.company_info.industry}</span>
          </div>
          {data.company_info.tech_stack.length > 0 && (
            <div className="flex flex-wrap gap-1 pt-1">
              {data.company_info.tech_stack.map((tool) => (
                <Badge key={tool} variant="secondary" className="text-[10px] font-normal py-0">
                  {tool}
                </Badge>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Funding */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-3.5 w-3.5 text-muted-foreground" />
          <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Funding
          </h4>
        </div>
        <div className="rounded-lg border bg-muted/20 p-3 space-y-1 text-xs">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-[10px]">
              {data.funding.stage}
            </Badge>
            <span className="font-semibold">{data.funding.amount}</span>
          </div>
          {data.funding.investors.length > 0 && (
            <p className="text-muted-foreground">
              Investors: {data.funding.investors.join(', ')}
            </p>
          )}
        </div>
      </div>

      {/* Recent News */}
      {data.recent_news.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Recent News
          </h4>
          <div className="space-y-2">
            {data.recent_news.map((item) => (
              <CollapsibleNewsItem key={item.title} {...item} />
            ))}
          </div>
        </div>
      )}

      {/* Pain Points */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Target className="h-3.5 w-3.5 text-muted-foreground" />
          <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Likely Pain Points
          </h4>
        </div>
        <ul className="space-y-1.5">
          {data.pain_points.map((point) => (
            <li key={point} className="flex items-start gap-2 text-xs">
              <span className="mt-0.5 h-2 w-2 shrink-0 rounded-full bg-orange-400" />
              <span className="text-foreground">{point}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Recommended Strategy */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Lightbulb className="h-3.5 w-3.5 text-muted-foreground" />
          <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Recommended Strategy
          </h4>
        </div>
        <div className="rounded-lg border bg-purple-50 dark:bg-purple-950/20 border-[rgba(28,63,255,0.25)] dark:border-purple-800 p-3 space-y-1">
          <Badge className="bg-[rgba(28,63,255,0.12)] text-[var(--color-cobalt-bright)] border-[rgba(28,63,255,0.25)] text-[10px]">
            {data.recommended_strategy.replace(/_/g, ' ')}
          </Badge>
          {strategyDescription && (
            <p className="text-xs text-muted-foreground leading-relaxed">{strategyDescription}</p>
          )}
        </div>
      </div>

      {/* ICP Fit Signals */}
      <div className="space-y-2">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          ICP Fit Signals
        </h4>
        <ul className="space-y-1.5">
          {data.icp_fit_signals.map((signal) => (
            <li key={signal} className="flex items-start gap-2 text-xs">
              <CheckCircle2 className="mt-0.5 h-3 w-3 shrink-0 text-green-500" />
              <span>{signal}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

// ─── Main panel ───────────────────────────────────────────────────────────

type EnrichmentPanelProps = {
  readonly isEnriched: boolean
  readonly isLoading: boolean
  readonly enrichmentData?: EnrichmentResult
  readonly onRunEnrichment: () => void
  readonly isDisabledForPlan?: boolean
}

export function EnrichmentPanel({
  isEnriched,
  isLoading,
  enrichmentData,
  onRunEnrichment,
  isDisabledForPlan = false,
}: EnrichmentPanelProps) {
  if (isLoading) {
    return <EnrichmentLoadingSkeleton />
  }

  if (isEnriched && enrichmentData) {
    return <EnrichedView data={enrichmentData} />
  }

  return (
    <UnenrichedState
      onRunEnrichment={onRunEnrichment}
      isDisabledForPlan={isDisabledForPlan}
    />
  )
}
